import { ChangeDetectorRef, Component, EventEmitter, HostBinding, inject, Input, Output } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import { Booking, BookingState, IUser, Review, SatisfactionScore } from '@luggagehero/shared/interfaces';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedReviewsService } from '@luggagehero/shared/services/reviews';
import { SharedUserService } from '@luggagehero/shared/services/users';
import { SharedWindowService } from '@luggagehero/shared/services/window';

import { BaseComponent } from '../../../core';
import { EXTERNAL_REVIEW_PLATFORMS } from '../../../utils/url.validators';

const noop = () => {
  /**/
};

export const createReview = (): Review => {
  return {} as Review;
};

@Component({
  template: '',
  standalone: false,
})
export abstract class ReviewBaseComponent extends BaseComponent implements ControlValueAccessor {
  /**
   * AM-4364 — maps a star rating (1–5) onto the legacy `satisfaction` score (0/1/2).
   * Validated against historical `all-reviews` Domo data (n = 189,855). Satisfaction is
   * being deprecated as a UX construct but stays populated for backwards compatibility.
   */
  public static starsToSatisfaction(stars: number): SatisfactionScore {
    if (stars <= 2) {
      return 0;
    }
    if (stars === 3) {
      return 1;
    }
    return 2;
  }

  protected cd = inject(ChangeDetectorRef);
  private reviewsService = inject(SharedReviewsService);
  private bookingService = inject(SharedBookingService);
  protected featureResolver = inject(FeatureResolver);
  protected userService = inject(SharedUserService);
  private windowService = inject(SharedWindowService);

  @Output() done: EventEmitter<unknown>;
  feedback = '';

  private _value: Review = createReview();
  private _externalReviewUrl: string;
  private _rating: number;
  private _booking: Booking;
  private _isLoading = false;
  // AM-4364 — re-entrancy guard for the terminal publish/save actions. The CTAs aren't disabled
  // during the in-flight request, so a double-tap (common on mobile) could call publishReview /
  // saveReview twice → two backend writes → a duplicate review notification, a duplicate
  // ReviewUpdated event, and a spurious "Updated review" Slack post. Ignore re-entrant calls.
  private _isSubmitting = false;
  // AM-4490 — tail of the serialized save() chain. The rating setter saves fire-and-forget on
  // every star tap, and AM-4490 now lets the external CTA be acted on immediately after rating.
  // Serializing all save()s through this chain means two quick taps can't run concurrent writes
  // (which on a new review could both addReview → duplicates, and could let an earlier rating
  // save land after — and clobber — finalizeRatingOnlyReview()'s published=true write).
  private _pendingSave: Promise<void> = Promise.resolve();
  private _isEditing = false;
  private _reviewRequested = false;
  private _user: IUser = {};
  public reviewLoaded = false;

  /**
   * AM-4364 Variant B — when true, the review surface fills the viewport and docks its CTA to
   * the bottom (matching the fullscreen green validate-booking view it morphs from: better
   * mobile UX, and no shape-change glitch during the transition). Set by the leave-feedback
   * action for the post-validation flow; off elsewhere, so the standard "Leave feedback"
   * modal keeps its compact, chrome'd layout. Adds the `lh-review--fullscreen` host class.
   */
  @Input() fullscreen = false;

  @HostBinding('class.lh-review--fullscreen')
  get isFullscreen(): boolean {
    return this.fullscreen;
  }

  // AM-4364 — snapshot of the originally-loaded review used to detect unsaved edits
  // when the user opens an already-published review for editing. Empty/null sentinels
  // when no review has been loaded yet.
  private _originalRating: number | null = null;
  private _originalHeadline = '';

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_) => void = noop;

  constructor() {
    super();
    this.done = new EventEmitter<unknown>();
  }

  set value(value: Review) {
    if (!value || value === this._value) {
      return;
    }
    this._value = value;

    this.onChangeCallback(value);
    this.cd.markForCheck();
    this.reviewLoaded = true;
  }
  get value(): Review {
    return this._value;
  }

  protected get user(): IUser {
    return this._user;
  }

  @Input() set booking(booking: Booking) {
    if (!booking || booking === this._booking) {
      return;
    }
    this._booking = booking;

    // create the user object once as it's used multiple places. Create a new from the booking if user not logged in
    this._user = this.userService.user
      ? this.userService.user
      : { email: this.booking.userEmail, name: this.booking.userName, picture: this.booking.userPicture };

    void this.reviewsService.getReview(booking._id).then((review) => {
      if (review) {
        console.log(`Review found for booking ${booking._id}`, {
          review,
          booking,
        });
        // Existing review
        this.value = review;
        this.rating = review.rating;
        this.isEditing = review.rating && !review.published;

        // AM-4364: snapshot for change detection in the edit-published flow
        if (review.published) {
          this._originalRating = review.rating ?? null;
          this._originalHeadline = review.headline ?? '';
        }
      } else {
        // New review
        const newReview = createReview();
        newReview.bookingId = this.booking._id;
        newReview.shopId = this.booking.shopId;
        newReview.userName = this._user.name;
        newReview.userPicture = this._user.picture || undefined;
        newReview.shopName = this.booking.shopName;
        newReview.published = false;

        console.log(`No review found for booking ${booking._id}`, {
          booking,
          newReview,
        });

        this.value = newReview;
      }
      this.markForCheck();
    });
  }
  get booking(): Booking {
    return this._booking;
  }

  @Input() set externalReviewUrl(value: string) {
    this._externalReviewUrl = value;
  }
  get externalReviewUrl(): string {
    return this._externalReviewUrl;
  }

  /**
   * AM-4479 — the URL the external CTA actually opens: the shop's effective external (Google/GBP)
   * review URL when present, otherwise the LuggageHero Trustpilot fallback (from the
   * `trustpilotReviewUrl` app setting; empty when that fallback is disabled). Google stays the
   * preferred target; Trustpilot is the company-level fallback for shops with no external profile.
   */
  get effectiveExternalReviewUrl(): string {
    if (this.externalReviewUrl) {
      return this.externalReviewUrl;
    }
    return this.featureResolver.trustpilotReviewUrl || '';
  }

  get isLoading(): boolean {
    return this._isLoading;
  }
  set isLoading(value: boolean) {
    this._isLoading = value;
    this.cd.markForCheck();
  }

  get isEditing(): boolean {
    return this._isEditing;
  }
  set isEditing(value: boolean) {
    this._isEditing = value;
    this.markForCheck();
  }

  get reviewRequested(): boolean {
    return this._reviewRequested;
  }
  set reviewRequested(value: boolean) {
    this._reviewRequested = value;
    this.markForCheck();
  }

  get rating(): number {
    return this._rating;
  }
  set rating(value: number) {
    this._rating = value;
    // AM-4364: keep `satisfaction` derived from the star rating so the legacy field stays
    // populated without traveler-facing mood UI. Mapping is the inverse of what the historic
    // mood-first flow would have produced.
    if (value > 0) {
      this.value.satisfaction = ReviewBaseComponent.starsToSatisfaction(value);
    }
    this.markForCheck();

    if (!this.value.published) {
      void this.save();
    }
  }

  get showEditableReview(): boolean {
    // AM-4364: editable until the user explicitly publishes. The new flow always offers the
    // rating + optional comment surface; the old "wait until mood is set" gating is gone.
    return !(this.value.published && !this.isEditing);
  }

  get showReadOnlyReview(): boolean {
    return this.value.published && !this.isEditing;
  }

  get showExternalReview(): boolean {
    if (AppConfig.IS_EXTERNAL_REVIEW_DISABLED) {
      return false;
    }
    // AM-4479 — gate on the effective target: the shop's external (Google/GBP) URL, or the
    // Trustpilot fallback when the shop has none. Empty only when there's no shop URL AND the
    // Trustpilot fallback is disabled, in which case there's no external ask to show.
    if (!this.effectiveExternalReviewUrl) {
      return false;
    }
    if (!this.value.rating) {
      return false;
    }
    // AM-4364 — once the user has been asked (opened the provider's URL, or skipped), don't
    // re-prompt; they get the editable internal review instead. The outcome lives on the
    // booking's UI-state (see recordExternalReviewOutcome).
    if (this.booking?.state?.externalReviewRequestedAt || this.booking?.state?.externalReviewSkippedAt) {
      return false;
    }
    // AM-4364 — gate on the INITIAL rating (the user's first impression, captured server-side
    // when the review is created): someone who initially rated 1–3★ and later edits up to 4–5★
    // must not suddenly be prompted to review externally. Legacy reviews created before
    // `initialRating` existed fall back to the current rating. The threshold is the
    // `externalReviewMinRating` app setting (default 4), tunable without a release.
    const gatingRating = this.value.initialRating ?? this.value.rating;
    return gatingRating >= this.featureResolver.externalReviewMinRating;
  }

  get externalReviewPlatform(): string {
    if (!this.externalReviewUrl) {
      // AM-4479 — no shop URL: we're on the Trustpilot fallback path when it's enabled
      // (effectiveExternalReviewUrl resolves to the LuggageHero Trustpilot page).
      return this.effectiveExternalReviewUrl ? 'Trustpilot' : null;
    }
    for (let i = 0; i < EXTERNAL_REVIEW_PLATFORMS.length; i++) {
      const platform = EXTERNAL_REVIEW_PLATFORMS[i];
      if (platform.urlPattern.test(this.externalReviewUrl)) {
        return platform.providerName;
      }
    }
    return null;
  }

  /** AM-4479 — true when the external prompt is the Trustpilot company-level fallback. */
  get isTrustpilotExternalReview(): boolean {
    return this.externalReviewPlatform === 'Trustpilot';
  }

  /** Heading copy key for the external CTA — Trustpilot fallback vs the default Google ask. */
  get externalReviewHeadingKey(): string {
    return this.isTrustpilotExternalReview ? 'ALSO_SHARE_ON_TRUSTPILOT' : 'ALSO_SHARE_ON_GOOGLE';
  }

  /** Subtitle copy key for the external CTA. */
  get externalReviewSubtitleKey(): string {
    return this.isTrustpilotExternalReview
      ? 'HELP_MORE_TRAVELERS_DISCOVER_LUGGAGEHERO'
      : 'HELP_MORE_PEOPLE_FIND_THIS_SHOP';
  }

  /** Primary button copy key for the external CTA. */
  get externalReviewButtonKey(): string {
    return this.isTrustpilotExternalReview ? 'LEAVE_TRUSTPILOT_REVIEW' : 'LEAVE_GOOGLE_REVIEW';
  }

  writeValue(value: Review) {
    if (value !== undefined) {
      this.value = value;
    }
  }

  registerOnChange(fn: (_) => void) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouchedCallback = fn;
  }

  /**
   * Directly set the satisfaction score. Retained for backwards compatibility — AM-4364
   * removed the mood-first UI flow that originally called this, but it remains a useful
   * programmatic entry point. The Intercom-on-Bad-mood routing was removed at the same time.
   */
  async setSatisfaction(value: SatisfactionScore): Promise<void> {
    this.value.satisfaction = value;
    return this.save();
  }

  async publishReview() {
    // AM-4364: guard against a double-tap (CTAs aren't disabled mid-request) re-publishing.
    if (this._isSubmitting) {
      return;
    }
    this._isSubmitting = true;
    try {
      // AM-4364: Wootric NPS prompt removed (NPS shifted to partners; out of scope for travelers).
      this.value.published = true;
      await this.save();

      // AM-4490: this is the optional internal-comment step's Submit/Skip handler, which the
      // template now renders only for NON-qualifying travelers — qualifying ones (external URL +
      // rating ≥ threshold) are routed straight to the external CTA right after rating, where the
      // review is finalized instead (see openExternalReview/skipExternalReview). So by the time
      // publishReview runs, there is no external ask to defer to: publish and close.
      this.done.emit();
    } finally {
      this._isSubmitting = false;
    }
  }

  /**
   * AM-4364 — user tapped the external review CTA ("Leave Google review", or "Review on
   * Trustpilot" on the AM-4479 fallback). Open the external URL in a new tab/window, finalize
   * the review, record the request, and close the modal.
   *
   * AM-4490 — for qualifying travelers this CTA now precedes the optional comment step, so it
   * (not the comment-step Submit) is where the rating-only review is finalized. The double-tap
   * guard mirrors publishReview: the CTA isn't disabled mid-request, so a re-tap could open a
   * second tab and double-record the outcome.
   *
   * Returns true only when the review was finalized, the outcome recorded, and the modal closed
   * (`done` emitted) — so subclasses can sequence follow-up prompts (e.g. the native store-review
   * ask) off the actual completion. Returns false on a re-entrant tap or a failed publish (the
   * CTA stays up for a genuine retry; nothing is recorded).
   */
  async openExternalReview(): Promise<boolean> {
    if (this._isSubmitting) {
      return false;
    }
    this._isSubmitting = true;
    try {
      // Open the URL first so it stays within the user gesture (avoids popup-blocking on web).
      // AM-4479 — `effectiveExternalReviewUrl` is the shop's external URL, or the Trustpilot
      // fallback when the shop has none.
      if (this.effectiveExternalReviewUrl) {
        this.windowService.open(this.effectiveExternalReviewUrl, '_blank');
      }
      if (!(await this.finalizeRatingOnlyReview())) {
        // AM-4490 — publish failed: keep the CTA on screen so the user can genuinely retry.
        // Recording the outcome here would set externalReviewRequestedAt and permanently
        // suppress the CTA (showExternalReview), stranding the review at published=false.
        return false;
      }
      await this.recordExternalReviewOutcome('requested');
      this.done.emit();
      return true;
    } finally {
      this._isSubmitting = false;
    }
  }

  /**
   * AM-4364 — user tapped "Skip" on the external CTA (Google or the AM-4479 Trustpilot
   * fallback). Finalize the review, record the skip (so we don't re-prompt and can target
   * follow-up campaigns) and close the modal without opening any URL.
   *
   * AM-4490 — see openExternalReview: the CTA now precedes the comment step, so skipping it is
   * still a completed review for qualifying travelers — finalize the rating-only review here.
   * Returns true only on full completion (finalized + recorded + closed), like openExternalReview.
   */
  async skipExternalReview(): Promise<boolean> {
    if (this._isSubmitting) {
      return false;
    }
    this._isSubmitting = true;
    try {
      if (!(await this.finalizeRatingOnlyReview())) {
        // AM-4490 — publish failed: leave the CTA up for a real retry (see openExternalReview).
        return false;
      }
      await this.recordExternalReviewOutcome('skipped');
      this.done.emit();
      return true;
    } finally {
      this._isSubmitting = false;
    }
  }

  /**
   * AM-4490 — publish the rating-only review when a qualifying traveler acts on the external
   * CTA (open or skip). Because the CTA now fires right after rating — ahead of the optional
   * comment step — this is the finalize point that the comment-step Submit/Skip (publishReview)
   * played before. Publishing keeps the rating counting as the review and keeps the
   * `published`-gated behaviour consistent with the old post-comment publish: the rating strip
   * stays suppressed on re-entry (`isPostValidationRatingActive` reads `!review.published`) and
   * the timeline offers the read-only/"Edit review" surface.
   *
   * Returns whether the review is published (true also when it already was — the non-qualifying
   * comment-step path). On a failed publish it rolls the optimistic `published` flag back and
   * returns false so the caller leaves the CTA up for a genuine retry instead of recording the
   * outcome (which would suppress the CTA forever) — the rating itself was already persisted on
   * the star tap, so nothing is lost.
   *
   * First drains the serialized save chain (`_pendingSave`): each star tap saves fire-and-forget
   * and persistReview() reassigns `this.value` to the backend echo, so we must wait for those to
   * settle before flipping `published` — otherwise a rating save resolving later would reassign
   * `this.value` and discard the published=true write. After draining, the publish is the last
   * save to run, on the settled review.
   */
  private async finalizeRatingOnlyReview(): Promise<boolean> {
    if (this.value.published) {
      return true;
    }
    try {
      await this._pendingSave;
    } catch {
      // A fire-and-forget rating save() rejected — its own flow owns that; we publish below.
    }
    this.value.published = true;
    try {
      await this.save();
      return true;
    } catch {
      this.value.published = false;
      return false;
    }
  }

  /**
   * AM-4364 — persist the external-review prompt outcome onto the booking's UI-state, so the
   * Google CTA isn't shown again and the user gets the editable internal review on re-entry.
   * Recorded on `booking.state` (alongside getDirectionsDone / showReceiptDone) via the
   * side-effect-free PUT /state path — unlike updating the review, which re-notifies partners.
   * `provider` captures which external profile was offered, for campaign segmentation.
   * Non-blocking: a failed save still leaves the in-memory state set for this session.
   */
  private async recordExternalReviewOutcome(outcome: 'requested' | 'skipped'): Promise<void> {
    if (!this.booking) {
      return;
    }
    const state: BookingState = this.booking.state ?? {
      getDirectionsDone: false,
      showReceiptDone: false,
      exploreExperiencesDone: false,
    };
    if (outcome === 'requested') {
      state.externalReviewRequestedAt = new Date();
    } else {
      state.externalReviewSkippedAt = new Date();
    }
    state.externalReviewProvider = this.externalReviewPlatform ?? undefined;
    this.booking.state = state;

    try {
      await this.bookingService.updateState(this.booking);
    } catch {
      // Non-blocking — the in-memory mutation suppresses the CTA this session; persistence
      // is retried the next time the user takes the action.
    }
  }

  /** Whether the user has typed something into the comment field — gates the Submit CTA. */
  get hasComment(): boolean {
    return !!this.value?.headline?.trim();
  }

  /** True when this modal is editing an already-published review (Save/Cancel mode). */
  get isEditingPublishedReview(): boolean {
    return this.value?.published === true;
  }

  /**
   * Whether the user has made any change vs. the snapshot taken when the review was
   * loaded. Drives the "Save review" CTA's enabled state. False when the review
   * isn't a published-edit (the Submit/Skip flow has its own gating).
   */
  get hasUnsavedChanges(): boolean {
    if (!this.isEditingPublishedReview) {
      return false;
    }
    const currentRating = this._rating ?? null;
    const currentHeadline = this.value?.headline ?? '';
    return currentRating !== this._originalRating || currentHeadline !== this._originalHeadline;
  }

  /**
   * Persist the in-progress edits to a published review and close. Same backend call
   * as `publishReview` but keeps `published=true` (it's already published) and emits
   * `done` on completion.
   */
  async saveReview(): Promise<void> {
    // AM-4364: guard against a double-tap re-saving (see publishReview).
    if (this._isSubmitting) {
      return;
    }
    this._isSubmitting = true;
    try {
      await this.save();
      this.done.emit();
    } finally {
      this._isSubmitting = false;
    }
  }

  /**
   * Discard local edits and close. We restore from the snapshot before emitting `done`
   * so the parent's bound review reference reflects the on-disk state.
   */
  cancelEdit(): void {
    if (this.isEditingPublishedReview) {
      this.value.rating = this._originalRating ?? undefined;
      this.value.headline = this._originalHeadline;
      this._rating = this._originalRating ?? undefined;
    }
    this.done.emit();
  }

  /**
   * AM-4364 — close the modal without leaving a review. Used by the docked dismiss link in
   * the no-rating state (the fullscreen review owns its own escape hatch instead of the
   * shared modal close button). No review is created.
   */
  dismiss(): void {
    this.done.emit();
  }

  async save() {
    // if (!this.rating && this.value.satisfaction > 1) {
    //   // Prefill rating to 5 start when people respond "Great"
    //   this.rating = 5;
    // }

    // AM-4490 — serialize saves so concurrent calls never overlap (see _pendingSave). The rating
    // setter fires save() fire-and-forget on every star tap; chaining guarantees each save sees
    // the prior one's result (e.g. the _id assigned by the first addReview, so a second tap
    // updates rather than creating a duplicate) and that finalizeRatingOnlyReview()'s
    // published=true write lands last.
    const previous = this._pendingSave;
    const run = (async (): Promise<void> => {
      // Wait for the previously-queued save to settle (ignore its outcome — its own caller owns
      // any error) so two saves never run concurrently.
      await previous.then(
        () => undefined,
        () => undefined,
      );
      await this.persistReview();
    })();
    // Advance the chain tail, swallowing this run's rejection so one failed save can't deadlock
    // later ones. Callers still get `run` (the real outcome) so finalize can react to failure.
    this._pendingSave = (async (): Promise<void> => {
      try {
        await run;
      } catch {
        // swallowed — the chain stays alive; the caller of `run` handles the failure
      }
    })();
    return run;
  }

  private async persistReview(): Promise<void> {
    if (this.rating > 0) {
      this.value.rating = this.rating;
    }

    this.value = await (this.value._id
      ? this.reviewsService.updateReview(this.value)
      : this.reviewsService.addReview(this.value));

    this.isEditing = false;
  }

  startEditing() {
    this.isEditing = true;
  }

  requestReview() {
    this.reviewRequested = true;
  }

  async submitFeedback() {
    this.value.feedback = this.feedback;
    this.value = await this.reviewsService.updateReview(this.value);
    // this.done.next();
  }

  private markForCheck() {
    // HACK: Ensures that star rating component is properly updated
    setTimeout(() => this.cd.markForCheck(), 100);
  }
}
